import { useMutation } from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import { AssetAdapter, AssetStatusType } from "@validereinc/domain";
import { useStickyState } from "../../../hooks/useStickyState";
import { ExceptionUtils } from "../../../utils/exception";

export const useUpdateAssetStatusAsync = ({
  jobKey,
  assetId,
  setIsUpdating,
  invalidate,
}: {
  jobKey: string;
  assetId: string;
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>;
  invalidate: () => void;
}): {
  handleUpdateAssetStatusAsync: (update: {
    status: AssetStatusType;
    start_date?: string;
    end_date?: string;
  }) => Promise<void>;
} => {
  const { toast } = useToast();
  const [_, setJobId] = useStickyState<string>("", jobKey);

  const mutation = useMutation({
    mutationFn: async ({
      assetId,
      data,
    }: {
      assetId: string;
      data: {
        status: AssetStatusType;
        start_date?: string;
        end_date?: string;
      };
    }) =>
      AssetAdapter.updateStatusAsync({
        data: {
          asset_id: assetId,
          status: data.status,
          start_date: data.start_date,
          end_date: data.end_date,
        },
      }),
    onSuccess: (data) => {
      setJobId(data.job.id);
      invalidate();
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to update status",
      });
    },
  });

  const handleUpdateAssetStatusAsync = async (update: {
    status: AssetStatusType;
    start_date?: string;
    end_date?: string;
  }) => {
    setIsUpdating(true);
    await mutation.mutateAsync({
      assetId,
      data: update,
    });
    setIsUpdating(false);
  };

  return {
    handleUpdateAssetStatusAsync,
  };
};
